import { typeRole, wfSectionTitleStyle, wfMutedStyle, wfHeroStyle, wfContinueStyle, wfGamesSubStyle, wfCardTitleStyle, wfCardSubStyle } from '@/styles/typography';
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Clock, X, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    mapApiProviderToComponent,
    excludeMaintenanceProviders,
    hasIconForProvider,
    getIconPathForProvider,
    type GameProvider,
    type GameProviderApiItem,
} from '@/features/games/data/gameProviders';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useScrollingClass } from '@/hooks/useScrollingClass';
import { playClickSound } from '@/utils/soundUtils';
import { navigationFromState } from '@/utils/navigateBack';
import { notificationService } from '@/services/notificationService';
import { getGameProviders, getCachedProvidersSync, preloadProviderIcons } from '@/features/games/services/gameProviderService';
import {
    appStoreSpacing,
    computeHomeGridLayout,
    fallbackHomeGridLayout,
    getHomeGridBreakpointConfig,
    homeGridLayoutEqual,
    type HomeGridLayout,
} from '@/features/games/utils/homeGridLayout';
import { getUserGameWallets, type GameWalletItem } from '@/features/finance/services/paymentService';
import { useWinTickerItems } from '@/features/games/hooks/useWinTickerItems';
import { useAuthModal } from '@/contexts/AuthModalContext';
import APP_CONFIG from '@/config/appConfig';
import OrderSummaryFooter from '@/layouts/components/Footer/OrderSummaryFooter';
import OrderSummaryModal from '@/features/finance/components/OrderSummaryModal';
import ManualDepositWarningModal from '@/features/finance/components/ManualDepositWarningModal';
import GameWithdrawModal from '@/features/finance/components/withdraw/GameWithdrawModal';
import {
    Button,
    FilterChip,
    GameTile,
    IconButton,
    SearchPill,
    Skeleton,
    WinTicker,
} from '@/components/ui';

import {
    buildHomeProviderTagFilters,
    CONTINUE_FALLBACK_COUNT,
    HOME_TAG_VISIBLE_MAX,
    isHomeProviderTagFilterActive,
    matchesHomeProviderTag,
    pickContinueFallbackProviders,
} from '@/features/games/utils/homeProviderFilters';

// DEMO MODE - REMOVE IN PRODUCTION
import DemoWrapper from '@/components/demo/DemoWrapper';
import { MODAL_BACKDROP_SHELL_CLASS, MODAL_PANEL_CLASS, modalPanelStyle } from '@/styles/modalTokens';
import {
    ANIMATION_REGISTRY,
    getSharedShineClockStyle,
    getWaveTileStyle,
} from '@/config/animationRegistry';
import { useRouteAnimation } from '@/contexts/AnimationContext';
import { useModalMotion } from '@/hooks/useModalMotion';

const MAX_DEPOSIT_AMOUNT = 10000;
const HOME_PROVIDER_COUNT_KEY = 'kiosk:home:providerCount';
const HOME_SKELETON_MIN_ROWS = 4;

function readHomeSkeletonTileCount(cols: number): number {
    if (typeof window === 'undefined') {
        return cols * HOME_SKELETON_MIN_ROWS;
    }
    const stored = Number.parseInt(sessionStorage.getItem(HOME_PROVIDER_COUNT_KEY) || '0', 10);
    const estimated = stored > 0 ? stored : cols * HOME_SKELETON_MIN_ROWS;
    return Math.max(cols * 2, Math.ceil(estimated / cols) * cols);
}

function readInitialProvidersFromCache(): { providers: GameProvider[]; loading: boolean } {
    if (typeof window === 'undefined') {
        return { providers: [], loading: true };
    }
    const cached = getCachedProvidersSync();
    if (cached?.success && cached.data?.items?.length) {
        const mapped = excludeMaintenanceProviders(
            cached.data.items
                .map(mapApiProviderToComponent)
                .filter((provider): provider is GameProvider => provider !== null)
        );
        if (mapped.length > 0) {
            return { providers: mapped, loading: false };
        }
    }
    return { providers: [], loading: true };
}

function homeViewportWidth(): number {
    return typeof document !== 'undefined'
        ? document.documentElement.clientWidth
        : typeof window !== 'undefined'
          ? window.innerWidth
          : 375;
}


/**
 * Home tidal wave plays once per Home mount (when content first becomes ready).
 * Stays "armed" for the rest of the mount so wave class/coords are not stripped
 * (stripping re-rendered the whole Home and looked like a scale jump).
 * After the sweep, marks `data-home-wave-done` on the shell via DOM — CSS kills
 * the animation without a React state update. Filter remounts inherit done → no replay.
 */
function useHomeWaveOnMount(
    enabled: boolean,
    contentReady: boolean,
    maxDiagonal: number,
    shellEl: HTMLElement | null
): boolean {
    const [armed, setArmed] = useState(false);
    const startedThisMountRef = useRef(false);
    const finishedRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            setArmed(false);
            startedThisMountRef.current = false;
            finishedRef.current = false;
            shellEl?.removeAttribute('data-home-wave-done');
            return;
        }
        if (!contentReady || startedThisMountRef.current) return;
        startedThisMountRef.current = true;
        finishedRef.current = false;
        setArmed(true);
    }, [enabled, contentReady, shellEl]);

    useEffect(() => {
        if (!armed || !shellEl || finishedRef.current) return undefined;
        shellEl.removeAttribute('data-home-wave-done');
        const sweepMs =
            ANIMATION_REGISTRY.wave.durationMs +
            ANIMATION_REGISTRY.wave.staggerMs * Math.max(0, maxDiagonal) +
            80;
        const t = window.setTimeout(() => {
            finishedRef.current = true;
            shellEl.setAttribute('data-home-wave-done', '');
        }, sweepMs);
        return () => window.clearTimeout(t);
    }, [armed, maxDiagonal, shellEl]);

    return armed && enabled;
}

/** Home browse grid: cols + slot from shell border-box width (stable while scrolling). */
function useHomeGridLayout(shellEl: HTMLDivElement | null) {
    const [layout, setLayout] = useState<HomeGridLayout>(() =>
        fallbackHomeGridLayout(homeViewportWidth())
    );

    useLayoutEffect(() => {
        if (!shellEl) return;

        let raf = 0;
        let lastBorderBox = 0;

        const applyFromBorderBox = (borderBox: number) => {
            if (borderBox <= 0) return;
            // Overlay scrollbar / sub-pixel / mobile chrome flicker while scrolling —
            // ignore tiny width noise so tiles don't reflow mid-scroll.
            if (lastBorderBox > 0 && Math.abs(borderBox - lastBorderBox) < 8) {
                return;
            }
            lastBorderBox = borderBox;

            const padHint = appStoreSpacing(borderBox);
            const contentWidth = Math.max(0, borderBox - 2 * padHint);
            const next = computeHomeGridLayout(
                contentWidth,
                getHomeGridBreakpointConfig(contentWidth),
                padHint
            );
            setLayout((prev) => (homeGridLayoutEqual(prev, next) ? prev : next));
        };

        const update = () => {
            applyFromBorderBox(Math.round(shellEl.getBoundingClientRect().width));
        };

        const schedule = () => {
            const borderBox = Math.round(shellEl.getBoundingClientRect().width);
            const delta = lastBorderBox > 0 ? Math.abs(borderBox - lastBorderBox) : Number.POSITIVE_INFINITY;
            // Desktop ↔ mobile jumps: apply before paint to avoid 1-frame tiny-tile flash.
            if (delta >= 40) {
                if (raf) {
                    window.cancelAnimationFrame(raf);
                    raf = 0;
                }
                applyFromBorderBox(borderBox);
                return;
            }
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                update();
            });
        };

        update();
        // border-box: padding (--home-pad-x) changes must NOT retrigger measure loops.
        const ro = new ResizeObserver(schedule);
        ro.observe(shellEl, { box: 'border-box' });

        // Mobile URL-bar show/hide fires resize; only react when width actually moves.
        let lastWinW = window.innerWidth;
        const onWinResize = () => {
            const w = window.innerWidth;
            const winDelta = Math.abs(w - lastWinW);
            if (winDelta < 8) return;
            lastWinW = w;
            if (winDelta >= 40) {
                if (raf) {
                    window.cancelAnimationFrame(raf);
                    raf = 0;
                }
                // Sync now (DevTools device mode / window drag), then one rAF to catch shell lag.
                update();
                raf = window.requestAnimationFrame(() => {
                    raf = 0;
                    update();
                });
                return;
            }
            schedule();
        };
        window.addEventListener('resize', onWinResize);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', onWinResize);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [shellEl]);

    return layout;
}

/** Shimmer placeholder matching Home `GameTile` (icon + label). */
function HomeGridTileSkeleton() {
    return (
        <div
            className="relative flex flex-col items-center shrink-0 min-w-0 w-[var(--gi)] max-w-[var(--gi)]"
            style={{ gap: 'var(--proto-chip-gap)' }}
            aria-hidden
        >
            <Skeleton className="h-[var(--gi)] w-[var(--gi)] shrink-0 rounded-[10px]" />
            <Skeleton
                className="w-[65%] max-w-[var(--gi)] rounded-md leading-snug"
                style={{ height: 'calc(var(--type-caption) * 1.375)' }}
            />
        </div>
    );
}

/** Reserve the same carousel footprint as loaded Continue playing cards. */
function continueSkeletonCardCount(viewportWidth: number): number {
    if (viewportWidth < 768) return 3; /* mobile peek: 2.2 visible + partial third */
    if (viewportWidth < 901) return 3;
    return 3;
}

function ContinueCarouselSkeleton({ label }: { label: string }) {
    const cardCount = continueSkeletonCardCount(
        typeof window !== 'undefined' ? window.innerWidth : 375
    );
    return (
        <div
            className="continue-carousel continue-carousel--loading continue-carousel--scrollable"
            aria-busy="true"
            aria-label={label}
        >
            <button
                type="button"
                className="continue-carousel__nav continue-carousel__nav--prev"
                aria-hidden
                tabIndex={-1}
                disabled
            >
                ‹
            </button>
            <div className="continue-carousel__viewport cosmos-scroll-hidden">
                <div className="continue-carousel__track">
                    {Array.from({ length: cardCount }, (_, i) => (
                        <div
                            key={i}
                            className="continue-carousel__card continue-carousel__card--skeleton flex flex-col items-center rounded-[8px]"
                            aria-hidden
                        >
                            <div
                                className="lobby-gi-ico relative shrink-0 overflow-hidden"
                                style={{ width: 'var(--continue-ico)', height: 'var(--continue-ico)' }}
                            >
                                <Skeleton className="absolute inset-0 h-full w-full rounded-none" />
                            </div>
                            <div
                                className="continue-carousel__meta flex flex-col items-center min-w-0 w-full text-center"
                                style={{ gap: 'var(--continue-meta-gap, calc(4px * var(--proto-scale)))' }}
                            >
                                <Skeleton
                                    className="w-[80%] rounded-md shrink-0"
                                    style={{ height: 'calc(var(--wf-card-title, var(--type-body)) * 1.3)' }}
                                />
                                <div className="continue-carousel__balance w-full flex items-center justify-center shrink-0">
                                    <Skeleton
                                        className="w-[70%] rounded-md"
                                        style={{ height: 'calc(var(--wf-card-sub, var(--type-caption)) * 1.3)' }}
                                    />
                                </div>
                            </div>
                            <Skeleton
                                className="continue-carousel__play cosmos-filter-chip-box cosmos-filter-chip--active !w-full md:!w-[5.2rem] md:!min-w-[5.2rem] shrink-0"
                                style={{ height: 'var(--continue-play-h)' }}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <button
                type="button"
                className="continue-carousel__nav continue-carousel__nav--next"
                aria-hidden
                tabIndex={-1}
                disabled
            >
                ›
            </button>
        </div>
    );
}

/** Continue-playing card icon: Skeleton overlay until the image finishes loading, then fade in. */
function ContinueCardIcon({ src, alt }: { src: string; alt: string }) {
    const [loaded, setLoaded] = useState(false);
    const [imgSrc, setImgSrc] = useState(src);
    useEffect(() => {
        setLoaded(false);
        setImgSrc(src);
    }, [src]);
    return (
        <div
            className="lobby-gi-ico relative shrink-0 overflow-hidden"
            style={{ width: 'var(--continue-ico)', height: 'var(--continue-ico)' }}
        >
            {!loaded && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
            <img
                // ponytail: ref bắt ảnh đã cached — onLoad không fire lại nên img kẹt opacity-0
                ref={(el) => { if (el?.complete && el.naturalWidth > 0) setLoaded(true); }}
                src={imgSrc}
                alt={alt}
                className={`block w-full h-full object-cover pointer-events-none select-none${loaded ? '' : ' opacity-0'}`}
                draggable={false}
                onLoad={() => setLoaded(true)}
                onError={() => {
                    if (imgSrc !== '/icons/egame.jpg') setImgSrc('/icons/egame.jpg');
                    else setLoaded(true);
                }}
            />
        </div>
    );
}

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines and uncomment the last line
// To enable demo mode: Uncomment the next 2 lines and comment out the last line
function KioskGameProvidersContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAuthenticated, requireAuth } = useAuthModal();
    const waveGridEnabled = useRouteAnimation('home.waveGrid');

    const [providers, setProviders] = useState<GameProvider[]>(() => readInitialProvidersFromCache().providers);
    const [isLoadingProviders, setIsLoadingProviders] = useState<boolean>(() => readInitialProvidersFromCache().loading);
    const [providersError, setProvidersError] = useState<string | null>(null);
    // Map to store amounts for each provider (id -> amount)
    const [providerAmounts, setProviderAmounts] = useState<Map<string, number>>(new Map());
    // Search + category filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [tagsExpanded, setTagsExpanded] = useState(false);
    // Order summary popup state
    const [showOrderSummary, setShowOrderSummary] = useState(false);
    // Manual deposit warning state
    const [showManualDepositWarning, setShowManualDepositWarning] = useState(false);
    // Game-detail modal (prototype UX: tap a tile → detail with PLAY / ADD FUNDS)
    const [detailProvider, setDetailProvider] = useState<GameProvider | null>(null);
    const {
        render: detailRender,
        exiting: detailExiting,
        backdropClass: detailBackdropClass,
        panelClass: detailPanelClass,
    } = useModalMotion(!!detailProvider);
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
    const [withdrawProviderId, setWithdrawProviderId] = useState<string | null>(null);
    // Authenticated: linked game wallets ("Continue playing")
    const [gameWallets, setGameWallets] = useState<GameWalletItem[]>([]);
    /** True while the continue-playing wallets are being fetched (reserves layout space) */
    const [walletsLoading, setWalletsLoading] = useState<boolean>(isAuthenticated);

    const [homeShellEl, setHomeShellEl] = useState<HTMLDivElement | null>(null);
    const homeBrowseGridRef = useRef<HTMLDivElement | null>(null);
    const homeBrowseChipsRef = useRef<HTMLDivElement | null>(null);
    const homeScrollRef = useRef<HTMLDivElement | null>(null);
    /** Chip viewport Y before filter change — corrected in useLayoutEffect after paint. */
    const pendingChipAnchorTopRef = useRef<number | null>(null);
    const measuredGridColsRef = useRef<number | null>(null);
    const [homeGridMinHeight, setHomeGridMinHeight] = useState(0);
    const homeGridLayout = useHomeGridLayout(homeShellEl);
    const lobbyCols = homeGridLayout.cols;
    const homeShellStyle = useMemo(
        () =>
            ({
                '--home-pad-x': `${homeGridLayout.padX}px`,
                '--proto-pad-x': `${homeGridLayout.padX}px`,
            }) as React.CSSProperties,
        [homeGridLayout.padX]
    );
    const homeGridStyle = useMemo(
        () =>
            ({
                '--home-cols': String(homeGridLayout.cols),
                '--home-gi-slot': `${homeGridLayout.slotGi}px`,
                '--home-gap': `${homeGridLayout.gap}px`,
                '--gi': `${homeGridLayout.slotGi}px`,
                gridTemplateColumns: `repeat(${homeGridLayout.cols}, minmax(0, 1fr))`,
                gap: `${homeGridLayout.gap}px`,
            }) as React.CSSProperties,
        [homeGridLayout]
    );

    const winItems = useWinTickerItems();
    const tickerItems = useMemo(
        () => winItems.map((item) => ({ player: item.identity, game: item.provider, amount: item.amount })),
        [winItems]
    );

    const preloadGameIcons = useCallback((providerItems: Array<{ code?: string }> = []) => {
        void preloadProviderIcons(providerItems.map((item) => item.code)).catch(() => {
            // Silent by design: icon preload must not affect UX flow.
        });
    }, []);

    useEffect(() => {
        if (providers.length > 0 && typeof window !== 'undefined') {
            sessionStorage.setItem(HOME_PROVIDER_COUNT_KEY, String(providers.length));
        }
    }, [providers.length]);

    // System-level scroll-to-top when the detail modal target changes page context
    useScrollToTop('providers', { behavior: 'smooth', delay: 100 });

    // Background comes from PublicLayout / AuthenticatedLayout (bg-cosmos + overlay)

    const refreshGameWallets = useCallback(() => {
        return getUserGameWallets()
            .then((response) => {
                if (response.success && response.data?.items) {
                    setGameWallets(response.data.items);
                }
            })
            .catch((error: unknown) => {
                console.error('[Home] Error loading game wallets:', error);
            });
    }, []);

    // Authenticated: load game wallets for the "Continue playing" strip
    useEffect(() => {
        if (!isAuthenticated) {
            setGameWallets([]);
            setWalletsLoading(false);
            return;
        }
        let cancelled = false;
        setWalletsLoading(true);
        refreshGameWallets().finally(() => {
            if (!cancelled) setWalletsLoading(false);
        });

        // Backend syncs provider balances shortly after /game-wallets/games — refresh once
        const syncRefreshTimer = window.setTimeout(() => {
            if (!cancelled) void refreshGameWallets();
        }, 5000);

        return () => {
            cancelled = true;
            window.clearTimeout(syncRefreshTimer);
        };
    }, [isAuthenticated, refreshGameWallets]);

    // Check if we should show login form (e.g., from "Back to Login" from forgot password)
    useEffect(() => {
        const showLogin = searchParams.get('showLogin');
        if (showLogin === 'true') {
            navigate('/login');
            // Remove the query parameter from URL
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, navigate]);

    // Fetch game providers from API
    useEffect(() => {
        const applyItems = (items: GameProviderApiItem[]): GameProvider[] =>
            excludeMaintenanceProviders(
                items
                    .map(mapApiProviderToComponent)
                    .filter((provider): provider is GameProvider => provider !== null)
            );

        const fetchProviders = async () => {
            // Check API cache first for instant display
            const cached = getCachedProvidersSync();

            if (cached && cached.success && cached.data?.items && cached.data.items.length > 0) {
                const mappedProviders = applyItems(cached.data.items);

                if (mappedProviders.length > 0) {
                    setProviders(mappedProviders);
                    setIsLoadingProviders(false);
                    preloadGameIcons(cached.data.items);
                    // Refresh in background (respect service cache TTL to avoid duplicate bursts)
                    getGameProviders(false).then(freshResponse => {
                        if (freshResponse.success && freshResponse.data?.items && freshResponse.data.items.length > 0) {
                            const freshProviders = applyItems(freshResponse.data.items);
                            if (freshProviders.length > 0) {
                                setProviders(freshProviders);
                                preloadGameIcons(freshResponse.data.items);
                            }
                        }
                    }).catch(() => { /* silent */ });
                    return;
                }
            }

            // No cache - fetch from API with loading state
            setIsLoadingProviders(true);
            setProvidersError(null);

            try {
                const response = await getGameProviders(false);

                if (response.success && response.data?.items && response.data.items.length > 0) {
                    setProviders(applyItems(response.data.items));
                    preloadGameIcons(response.data.items);
                } else {
                    setProvidersError('No game providers available. Please try again later.');
                }
            } catch (error: any) {
                console.error('[Home] Error fetching game providers:', error);
                setProvidersError('Failed to load game providers. Please try again later.');
            } finally {
                setIsLoadingProviders(false);
            }
        };

        fetchProviders();
    }, [preloadGameIcons]);

    // Handle amount changes for providers – memoized to prevent child re-renders
    const handleAmountChange = useCallback((providerId: string, amount: number) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            newMap.set(providerId, Math.max(0, Math.min(amount, MAX_DEPOSIT_AMOUNT)));
            return newMap;
        });
    }, []);

    const handleIncrement = useCallback((providerId: string) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            const currentAmount = newMap.get(providerId) || 0;
            const jumpAmount = APP_CONFIG.jumpingEachClick;
            const newAmount = Math.min(currentAmount + jumpAmount, MAX_DEPOSIT_AMOUNT);
            newMap.set(providerId, newAmount);
            return newMap;
        });
    }, []);

    const handleDecrement = useCallback((providerId: string) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            const currentAmount = newMap.get(providerId) || 0;
            if (currentAmount > 0) {
                const jumpAmount = APP_CONFIG.jumpingEachClick;
                const newAmount = Math.max(currentAmount - jumpAmount, 0);
                newMap.set(providerId, newAmount);
            } else {
                newMap.set(providerId, 0);
            }
            return newMap;
        });
    }, []);

    const handleResetAll = useCallback(() => {
        playClickSound();
        setProviderAmounts(new Map());
        setShowOrderSummary(false);
    }, []);

    const handleRemoveItem = useCallback((providerId: string) => {
        playClickSound();
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            newMap.delete(providerId);
            return newMap;
        });
    }, []);

    /**
     * Gets list of manual game providers that have amounts > 0.
     */
    const getSelectedManualProviders = () => {
        return providers.filter(p => {
            const amount = providerAmounts.get(p.id) || 0;
            return amount > 0 && p.depositType === 'manual';
        });
    };

    /**
     * Gets total amount for selected manual providers.
     */
    const getManualProvidersTotalAmount = () => {
        return getSelectedManualProviders().reduce((sum, p) => sum + (providerAmounts.get(p.id) || 0), 0);
    };

    /**
     * Saves pending game amounts, then gates auth and opens Deposit (wallet-only).
     * Crypto/gateway checkout is no longer used for game funding.
     */
    const executeProceedToFunding = () => {
        // Save provider amounts to sessionStorage
        const amountsArray = Array.from(providerAmounts.entries())
            .filter(([_, amount]) => amount > 0)
            .map(([providerId, amount]) => ({ providerId, amount }));

        if (amountsArray.length > 0) {
            const pendingData = {
                amounts: amountsArray,
                timestamp: Date.now(),
                source: 'continue_button'
            };

            try {
                sessionStorage.setItem('pending_deposit_amounts', JSON.stringify(pendingData));
                console.log('💾 Saved pending deposit amounts to sessionStorage:', pendingData);
            } catch (error) {
                console.error('Failed to save to sessionStorage:', error);
            }
        }

        // Close popups
        setShowOrderSummary(false);
        setShowManualDepositWarning(false);
        setDetailProvider(null);

        requireAuth(() => {
            navigate('/account/finance/deposit');
        });
    };

    // Handle proceed to funding
    const handleProceedToFunding = () => {
        playClickSound();

        // Check if any selected provider is manual
        const manualProviders = getSelectedManualProviders();
        if (manualProviders.length > 0) {
            // Show manual deposit warning before proceeding
            setShowManualDepositWarning(true);
            return;
        }

        // No manual games → proceed immediately
        executeProceedToFunding();
    };

    // Memoized calculations – avoid recalculating on every render
    const totalAmount = useMemo(
        () => Array.from(providerAmounts.values()).reduce((sum, amount) => sum + amount, 0),
        [providerAmounts]
    );

    const tagFilters = useMemo(() => buildHomeProviderTagFilters(providers), [providers]);

    const hasTagOverflow = tagFilters.length > HOME_TAG_VISIBLE_MAX;
    const visibleTagFilters =
        tagsExpanded || !hasTagOverflow ? tagFilters : tagFilters.slice(0, HOME_TAG_VISIBLE_MAX);
    const hiddenTagCount =
        hasTagOverflow && !tagsExpanded ? tagFilters.length - HOME_TAG_VISIBLE_MAX : 0;

    useEffect(() => {
        if (!isHomeProviderTagFilterActive(categoryFilter, tagFilters)) {
            setCategoryFilter('all');
        }
    }, [categoryFilter, tagFilters]);

    const filteredCards = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return providers.filter((card) => {
            if (q && !(
                card.name.toLowerCase().includes(q) ||
                (card.tagline && card.tagline.toLowerCase().includes(q)) ||
                (card.code && card.code.toLowerCase().includes(q)) ||
                (card.clientTag && card.clientTag.toLowerCase().includes(q))
            )) {
                return false;
            }
            if (categoryFilter === 'all') return true;
            return matchesHomeProviderTag(card, categoryFilter);
        });
    }, [providers, searchQuery, categoryFilter]);

    // Lock browse-grid height to the full "All" layout so HOT/tag filters don't collapse the page.
    useLayoutEffect(() => {
        if (isLoadingProviders || searchQuery.trim()) return;
        if (categoryFilter !== 'all') return;
        const el = homeBrowseGridRef.current;
        if (!el) return;

        const applyHeight = () => {
            const h = Math.round(el.getBoundingClientRect().height);
            if (h <= 0) return;
            setHomeGridMinHeight((prev) => {
                if (measuredGridColsRef.current !== lobbyCols) {
                    measuredGridColsRef.current = lobbyCols;
                    return h;
                }
                return h > prev ? h : prev;
            });
        };

        applyHeight();
        // Images / fonts can grow the grid after first paint.
        const ro = new ResizeObserver(applyHeight);
        ro.observe(el);
        return () => ro.disconnect();
    }, [categoryFilter, filteredCards, homeGridLayout, isLoadingProviders, lobbyCols, searchQuery]);

    // After filter change: keep chips fixed in the viewport (scroll height may shrink).
    useLayoutEffect(() => {
        const before = pendingChipAnchorTopRef.current;
        if (before == null) return;
        pendingChipAnchorTopRef.current = null;
        const scrollEl = homeScrollRef.current;
        const chipsEl = homeBrowseChipsRef.current;
        if (!scrollEl || !chipsEl) return;
        const after = chipsEl.getBoundingClientRect().top;
        const delta = after - before;
        if (Math.abs(delta) >= 1) {
            scrollEl.scrollTop += delta;
        }
    }, [categoryFilter, filteredCards.length]);

    /*
     * Desktop: fast vertical scroll keeps :hover on tiles under the cursor.
     * Hover uses transform (+ Continue z-index) so those layers paint over neighbors.
     * Flag the scrollport while scrolling so CSS can drop the lift.
     */
    useScrollingClass(homeScrollRef, 360);

    // All linked My-games wallets (for play / lookup)
    const linkedWallets = useMemo(
        () => gameWallets.filter((w) => !!w.account?.gameUsername && w.game),
        [gameWallets]
    );

    /** Continue playing: linked games sorted by balance — manual carousel scroll. */
    const continuePlayingWallets = useMemo(
        () =>
            [...linkedWallets].sort(
                (a, b) =>
                    (b.balance?.inGameBalance ?? 0) - (a.balance?.inGameBalance ?? 0)
            ),
        [linkedWallets]
    );

    /**
     * When the user has no linked accounts, keep the Continue strip filled with
     * tagged/popular games so skeleton → empty does not collapse the layout.
     */
    const continueFallbackProviders = useMemo(() => {
        if (continuePlayingWallets.length > 0) return [];
        return pickContinueFallbackProviders(providers, CONTINUE_FALLBACK_COUNT);
    }, [continuePlayingWallets.length, providers]);

    /** Replay tidal wave each time Home mounts (when content is ready) */
    const homeWaveContentReady =
        continuePlayingWallets.length > 0 ||
        continueFallbackProviders.length > 0 ||
        (!isLoadingProviders && filteredCards.length > 0);
    const homeSkeletonTileCount = useMemo(
        () => readHomeSkeletonTileCount(lobbyCols),
        [lobbyCols]
    );

    /**
     * The Continue playing strip is the visual "row 0" above the grid when the
     * user is signed in — offset grid wave rows by 1 so the diagonal wave
     * flows continuously from the strip down through the grid.
     */
    const continueStripLoading =
        isAuthenticated &&
        (walletsLoading ||
            (!walletsLoading &&
                continuePlayingWallets.length === 0 &&
                isLoadingProviders &&
                providers.length === 0));
    const showContinueWallets =
        isAuthenticated && !walletsLoading && continuePlayingWallets.length > 0;
    const showContinueFallback =
        isAuthenticated &&
        !walletsLoading &&
        continuePlayingWallets.length === 0 &&
        continueFallbackProviders.length > 0;
    const showContinuePlaying = showContinueWallets || showContinueFallback;
    const showContinueBlock = continueStripLoading || showContinuePlaying;
    /** Giữ class scrollable ổn định (mobile + desktop) — tránh toggle sau đo overflow. */
    const homeContinueCarouselScrollable = showContinueBlock;
    const waveRowOffset = showContinuePlaying ? 1 : 0;

    const waveMaxDiagonal = useMemo(() => {
        const cols = Math.max(1, lobbyCols);
        const gridRows = Math.max(1, Math.ceil(filteredCards.length / cols));
        const gridDiag = waveRowOffset + (gridRows - 1) + (cols - 1);
        const continueCount = showContinueWallets
            ? continuePlayingWallets.length
            : showContinueFallback
              ? continueFallbackProviders.length
              : 0;
        const continueDiag = showContinuePlaying ? Math.max(0, continueCount - 1) : 0;
        return Math.max(gridDiag, continueDiag);
    }, [
        continueFallbackProviders.length,
        continuePlayingWallets.length,
        filteredCards.length,
        lobbyCols,
        showContinueFallback,
        showContinuePlaying,
        showContinueWallets,
        waveRowOffset,
    ]);

    /**
     * Shared shine clock for the browse grid. Recalculated when the visible set
     * changes so remounted tiles stay phase-locked to each other (and to header `.btn-shine`).
     */
    const homeBrowseGridStyle = useMemo(() => {
        if (!waveGridEnabled) return homeGridStyle;
        return {
            ...homeGridStyle,
            ...getSharedShineClockStyle({
                '--shine-stagger': `${ANIMATION_REGISTRY.gridShine.staggerMs}ms`,
            }),
        } as React.CSSProperties;
        // Intentionally keyed to filter/layout — not filteredCards identity each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeGridStyle, categoryFilter, searchQuery, lobbyCols, filteredCards.length, waveGridEnabled]);

    // Wave must still play when providers hydrate from cache (common after first visit).
    // ff22a5d gated this with !bootedFromProviderCache and effectively killed home wave.
    const playHomeWave = useHomeWaveOnMount(
        waveGridEnabled,
        homeWaveContentReady,
        waveMaxDiagonal,
        homeShellEl
    );

    const continueCarouselRef = useRef<HTMLDivElement | null>(null);
    const [canScrollContinuePrev, setCanScrollContinuePrev] = useState(false);
    const [canScrollContinueNext, setCanScrollContinueNext] = useState(false);
    const [continueCarouselOverflow, setContinueCarouselOverflow] = useState(() => isAuthenticated);

    const updateContinueCarouselNav = useCallback(() => {
        const el = continueCarouselRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setContinueCarouselOverflow(scrollWidth > clientWidth + 2);
        setCanScrollContinuePrev(scrollLeft > 4);
        setCanScrollContinueNext(scrollLeft + clientWidth < scrollWidth - 4);
    }, []);

    useLayoutEffect(() => {
        const el = continueCarouselRef.current;
        if (!el) return;
        updateContinueCarouselNav();
        const ro = new ResizeObserver(updateContinueCarouselNav);
        ro.observe(el);
        el.addEventListener('scroll', updateContinueCarouselNav, { passive: true });
        return () => {
            ro.disconnect();
            el.removeEventListener('scroll', updateContinueCarouselNav);
        };
    }, [
        continueStripLoading,
        continuePlayingWallets.length,
        continueFallbackProviders.length,
        updateContinueCarouselNav,
    ]);

    const scrollContinueCarousel = (direction: -1 | 1) => {
        const el = continueCarouselRef.current;
        if (!el) return;
        playClickSound();
        const card = el.querySelector<HTMLElement>('.continue-carousel__card');
        const cardWidth = card?.offsetWidth ?? 0;
        const gap = Number.parseFloat(getComputedStyle(el.querySelector('.continue-carousel__track') ?? el).gap) || 0;
        const step = cardWidth > 0 ? cardWidth + gap : Math.round(el.clientWidth * 0.9);
        el.scrollBy({ left: direction * step, behavior: 'smooth' });
    };

    const normalizeCode = (code: string | undefined | null) =>
        (code || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '');

    const launchWallet = (wallet: GameWalletItem) => {
        playClickSound();
        const gameUrl = wallet.game?.gameUrl;
        if (gameUrl) {
            navigate(`/account/games/play?url=${encodeURIComponent(gameUrl)}`);
        } else {
            navigate('/account/games');
        }
    };

    const handlePlay = (provider: GameProvider) => {
        playClickSound();
        requireAuth(() => {
            const wallet = linkedWallets.find(
                w => normalizeCode(w.game?.code) === normalizeCode(provider.code) && !!w.game?.gameUrl
            );
            if (wallet?.game?.gameUrl) {
                navigate(`/account/games/play?url=${encodeURIComponent(wallet.game.gameUrl)}`);
            } else {
                navigate('/account/games');
            }
        });
    };

    /** Prototype gmFund: close modal → requireAuth → deposit with game pre-selected, amount chosen on deposit page */
    const handleAddFundsFromDetail = () => {
        if (!detailProvider) return;
        const provider = detailProvider;
        setDetailProvider(null);
        requireAuth(() => {
            navigate(`/account/finance/deposit?provider=${encodeURIComponent(provider.id)}`, {
                ...navigationFromState(location.pathname),
            });
        });
    };

    const handleWithdrawFromDetail = () => {
        if (!detailProvider) return;
        const provider = detailProvider;
        setDetailProvider(null);
        requireAuth(() => {
            setWithdrawProviderId(provider.id);
            setWithdrawModalOpen(true);
        });
    };

    const openDetail = (provider: GameProvider) => {
        playClickSound();
        if (provider.status === 'inactive') {
            notificationService.info(
                t('home.comingSoonToast', '{{name}} is coming soon — not available yet.', {
                    name: provider.name,
                })
            );
            return;
        }
        setDetailProvider(provider);
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value.slice(0, 40));
    };

    const setFilter = (filter: string) => {
        playClickSound();
        if (filter === categoryFilter) return;
        const chipsEl = homeBrowseChipsRef.current;
        if (chipsEl) {
            pendingChipAnchorTopRef.current = chipsEl.getBoundingClientRect().top;
        }
        setCategoryFilter(filter);
    };

    return (<div className="h-full max-h-full flex flex-col relative min-w-0 overflow-hidden bg-transparent">
        {/* Body scrolls; win ticker stays at bottom (Prototype Home) */}
        <main className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
            <div
                ref={homeScrollRef}
                className="lobby-home-scroll flex-1 min-h-0 cosmos-scroll-y overflow-x-hidden overscroll-contain [scrollbar-gutter:stable]"
            >
                {/* Prototype .bd tokens × --proto-scale (canvas 1100 → fullscreen) */}
                <div ref={setHomeShellEl} className="lobby-bd lobby-home-bd" style={homeShellStyle}>
                    {/* Order: hero|Continue → chips → grid — tick is outside .bd */}
                    {!isAuthenticated ? (
                        <section
                            className="lobby-home-section lobby-home-hero"
                            aria-labelledby="home-hero-title"
                        >
                            <h1
                                id="home-hero-title"
                                className="font-display text-ink font-bold tracking-tight leading-snug"
                                style={wfHeroStyle}
                            >
                                {t('home.heroTitle', 'One wallet. All your games.')}
                            </h1>
                            <p
                                className="font-ui text-caption-muted leading-snug"
                                style={wfMutedStyle}
                            >
                                {t(
                                    'home.heroSubtitle',
                                    'Browse everything — sign in only when you play or add funds.'
                                )}
                            </p>
                        </section>
                    ) : showContinueBlock ? (
                            <section
                            aria-label={
                                showContinueFallback
                                    ? t('home.popularGames', 'Popular games')
                                    : t('home.continuePlaying', 'Continue playing')
                            }
                            className="lobby-home-section overflow-x-clip"
                            data-tour-id="tour.home.continue"
                        >
                            <h2
                                className="font-display font-bold text-ink"
                                style={wfContinueStyle}
                            >
                                {showContinueFallback
                                    ? t('home.popularGames', 'Popular games')
                                    : t('home.continuePlaying', 'Continue playing')}
                            </h2>
                            {continueStripLoading ? (
                                <ContinueCarouselSkeleton label={t('common.loading', 'Loading…')} />
                            ) : (
                            <div
                                    className={[
                                        'continue-carousel',
                                        homeContinueCarouselScrollable && 'continue-carousel--scrollable',
                                        canScrollContinuePrev && 'continue-carousel--has-prev',
                                        canScrollContinueNext && 'continue-carousel--has-next',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    <button
                                        type="button"
                                        className="continue-carousel__nav continue-carousel__nav--prev"
                                        aria-label={t('home.continueScrollPrev', 'Scroll left')}
                                        disabled={!continueCarouselOverflow || !canScrollContinuePrev}
                                        onClick={() => scrollContinueCarousel(-1)}
                                    >
                                        ‹
                                    </button>
                                    <div
                                        ref={continueCarouselRef}
                                        className="continue-carousel__viewport cosmos-scroll-hidden"
                                    >
                                        <div className="continue-carousel__track">
                                        {showContinueWallets
                                            ? continuePlayingWallets.map((wallet, cardIdx) => {
                                            const code = wallet.game?.code;
                                            const balance = wallet.balance?.inGameBalance ?? 0;
                                            const isComingSoon = wallet.game?.status === 'inactive';
                                            return (
                                                <div
                                                    key={wallet.game?.id ?? wallet.account?.id ?? cardIdx}
                                                    className={`${playHomeWave ? `${ANIMATION_REGISTRY.wave.className} ` : ''}continue-carousel__card shrink-0 flex flex-col items-center rounded-[8px] cursor-pointer${isComingSoon ? ' opacity-60 saturate-50' : ''}`}
                                                    style={
                                                        playHomeWave
                                                            ? (getWaveTileStyle(0, cardIdx) as React.CSSProperties)
                                                            : undefined
                                                    }
                                                    onClick={() => {
                                                        if (isComingSoon) {
                                                            playClickSound();
                                                            notificationService.info(
                                                                t(
                                                                    'home.comingSoonToast',
                                                                    '{{name}} is coming soon — not available yet.',
                                                                    {
                                                                        name:
                                                                            wallet.game?.name ||
                                                                            t('home.comingSoon', 'Coming soon'),
                                                                    }
                                                                )
                                                            );
                                                            return;
                                                        }
                                                        const p = providers.find(
                                                            (x) =>
                                                                normalizeCode(x.code) === normalizeCode(code)
                                                        );
                                                        if (p) openDetail(p);
                                                    }}
                                                >
                                                    <ContinueCardIcon
                                                        src={getIconPathForProvider(code || '')}
                                                        alt={wallet.game?.name || 'Game'}
                                                    />
                                                    <div
                                                        className="continue-carousel__meta flex flex-col items-center min-w-0 w-full text-center"
                                                        style={{ gap: 'var(--continue-meta-gap, calc(4px * var(--proto-scale)))' }}
                                                    >
                                                        <p
                                                            className="continue-carousel__title font-ui font-bold text-ink truncate w-full"
                                                            style={wfCardTitleStyle}
                                                        >
                                                            {wallet.game?.name || 'Game'}
                                                        </p>
                                                        <div className="continue-carousel__balance flex flex-row flex-nowrap items-baseline gap-x-1.5 min-w-0">
                                                            <span
                                                                className="continue-carousel__balance-amount font-money font-bold text-money-out-soft tabular-nums shrink-0 whitespace-nowrap"
                                                                style={wfCardSubStyle}
                                                            >
                                                                {`$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                            </span>
                                                            <span className="continue-carousel__balance-sep shrink-0" aria-hidden>
                                                                ·
                                                            </span>
                                                            <span className="continue-carousel__balance-label font-ui font-semibold text-money-out-soft shrink-0 whitespace-nowrap">
                                                                {t('home.inGameLabel', 'in game')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <FilterChip
                                                        shape="box"
                                                        uniform
                                                        active
                                                        className="continue-carousel__play !w-full md:!w-auto !text-[length:var(--wf-games-tab)]"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            launchWallet(wallet);
                                                        }}
                                                    >
                                                        <Play className="continue-carousel__play-ico" fill="currentColor" aria-hidden />
                                                        {t('home.play', 'Play')}
                                                    </FilterChip>
                                                </div>
                                            );
                                        })
                                            : continueFallbackProviders.map((provider, cardIdx) => (
                                                <div
                                                    key={provider.id}
                                                    className={`${playHomeWave ? `${ANIMATION_REGISTRY.wave.className} ` : ''}continue-carousel__card shrink-0 flex flex-col items-center rounded-[8px] cursor-pointer`}
                                                    style={
                                                        playHomeWave
                                                            ? (getWaveTileStyle(0, cardIdx) as React.CSSProperties)
                                                            : undefined
                                                    }
                                                    onClick={() => openDetail(provider)}
                                                >
                                                    <ContinueCardIcon
                                                        src={
                                                            hasIconForProvider(provider.code)
                                                                ? provider.icon
                                                                : getIconPathForProvider(provider.code || '')
                                                        }
                                                        alt={provider.name}
                                                    />
                                                    <div
                                                        className="continue-carousel__meta flex flex-col items-center min-w-0 w-full text-center"
                                                        style={{ gap: 'var(--continue-meta-gap, calc(4px * var(--proto-scale)))' }}
                                                    >
                                                        <p
                                                            className="continue-carousel__title font-ui font-bold text-ink truncate w-full"
                                                            style={wfCardTitleStyle}
                                                        >
                                                            {provider.name}
                                                        </p>
                                                        <div className="continue-carousel__balance">
                                                            <span
                                                                className="continue-carousel__balance-label font-ui font-semibold text-money-out-soft"
                                                                style={wfCardSubStyle}
                                                            >
                                                                {provider.clientTag?.trim() ||
                                                                    t('home.suggestedLabel', 'Suggested')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <FilterChip
                                                        shape="box"
                                                        uniform
                                                        active
                                                        className="continue-carousel__play !w-full md:!w-auto !text-[length:var(--wf-games-tab)]"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDetail(provider);
                                                        }}
                                                    >
                                                        <Play className="continue-carousel__play-ico" fill="currentColor" aria-hidden />
                                                        {t('home.play', 'Play')}
                                                    </FilterChip>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="continue-carousel__nav continue-carousel__nav--next"
                                        aria-label={t('home.continueScrollNext', 'Scroll right')}
                                        disabled={!continueCarouselOverflow || !canScrollContinueNext}
                                        onClick={() => scrollContinueCarousel(1)}
                                    >
                                        ›
                                    </button>
                                </div>
                            )}
                        </section>
                    ) : null}

                    <section
                        aria-label={t('home.browseGames', 'Browse games')}
                        className="lobby-home-section lobby-home-browse-section min-w-0 border-t border-border-ice/60"
                    >
                        <h2
                            className="font-display font-bold text-ink"
                            style={wfContinueStyle}
                        >
                            {t('home.browseGames', 'Browse games')}
                        </h2>

                        <div
                            ref={homeBrowseChipsRef}
                            className="lobby-browse-chips flex items-center min-w-0 w-full cosmos-scroll-x cosmos-scroll-hidden"
                            style={{ gap: 'calc(8px * var(--proto-scale))' }}
                            data-tour-id="tour.home.filters"
                        >
                                <FilterChip
                                    active={categoryFilter === 'all'}
                                    shape="box"
                                    uniform
                                    onClick={() => setFilter('all')}
                                >
                                    {t('home.filters.all', 'All')}
                                </FilterChip>
                                {visibleTagFilters.map((tag) => (
                                    <FilterChip
                                        key={tag.id}
                                        active={categoryFilter === tag.id}
                                        shape="box"
                                        uniform
                                        onClick={() => setFilter(tag.id)}
                                    >
                                        {tag.label}
                                    </FilterChip>
                                ))}
                                {hiddenTagCount > 0 ? (
                                    <FilterChip
                                        active={false}
                                        shape="box"
                                        onClick={() => {
                                            playClickSound();
                                            setTagsExpanded(true);
                                        }}
                                    >
                                        {t('home.filters.moreTags', '+{{count}} more', { count: hiddenTagCount })}
                                    </FilterChip>
                                ) : null}
                                {tagsExpanded && hasTagOverflow ? (
                                    <FilterChip
                                        active={false}
                                        shape="box"
                                        onClick={() => {
                                            playClickSound();
                                            setTagsExpanded(false);
                                        }}
                                    >
                                        {t('home.filters.lessTags', 'Less')}
                                    </FilterChip>
                                ) : null}
                        </div>

                        <SearchPill
                            variant="toolbar"
                            compact
                            shape="box"
                            clearable
                            placeholder={t('games.searchGamesPlaceholder', 'Search games…')}
                            value={searchQuery}
                            maxLength={40}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            containerClassName="lobby-browse-search w-full max-w-full"
                            data-tour-id="tour.home.search"
                        />

                    {isLoadingProviders && (
                        <div className="lobby-home-grid" style={homeGridStyle} data-tour-id="tour.home.grid">
                            {Array.from({ length: homeSkeletonTileCount }, (_, i) => (
                                <HomeGridTileSkeleton key={`home-skel-${i}`} />
                            ))}
                        </div>
                    )}

                    {!isLoadingProviders && providersError && (
                        <div className="text-center py-2">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-control border border-danger/40 bg-danger/15">
                                <span className={`font-ui text-danger-soft font-medium ${typeRole.body}`}>{providersError}</span>
                            </div>
                        </div>
                    )}

                    {!isLoadingProviders && providers.length > 0 && (
                        <div
                            className="lobby-home-grid-slot"
                            style={
                                homeGridMinHeight > 0 && !searchQuery.trim()
                                    ? { minHeight: homeGridMinHeight }
                                    : undefined
                            }
                        >
                            {filteredCards.length > 0 ? (
                                <div
                                    key={`home-grid-${categoryFilter}-${searchQuery.trim()}`}
                                    ref={homeBrowseGridRef}
                                    className="lobby-home-grid"
                                    style={homeBrowseGridStyle}
                                    data-tour-id="tour.home.grid"
                                >
                                    {filteredCards.map((provider, index) => {
                                        const isComingSoon = provider.status === 'inactive';
                                        return (
                                        <GameTile
                                            key={provider.id}
                                            name={provider.name}
                                            imageUrl={hasIconForProvider(provider.code) ? provider.icon : undefined}
                                            hot={!!provider.clientTag}
                                            comingSoon={isComingSoon}
                                            comingSoonLabel={t('home.comingSoon', 'Coming soon')}
                                            onClick={() => openDetail(provider)}
                                            waveRow={
                                                playHomeWave
                                                    ? waveRowOffset + Math.floor(index / lobbyCols)
                                                    : undefined
                                            }
                                            waveCol={index % lobbyCols}
                                            shineRow={
                                                waveGridEnabled
                                                    ? Math.floor(index / lobbyCols)
                                                    : undefined
                                            }
                                            shineCol={index % lobbyCols}
                                        />
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <div className="inline-flex flex-col items-center gap-3 px-5 py-4 cosmos-panel">
                                        <span className={`font-ui text-body-muted font-medium ${typeRole.body}`}>
                                            {searchQuery.trim()
                                                ? t('home.noSearchResults', 'No games found matching "{{query}}"', { query: searchQuery })
                                                : t('home.noFilterResults', 'No games in this category')}
                                        </span>
                                        <Button
                                            variant="tertiary"
                                            size="sm"
                                            onClick={() => { playClickSound(); setSearchQuery(''); setCategoryFilter('all'); }}
                                        >
                                            {t('home.clearFilters', 'Clear filters')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!isLoadingProviders && providers.length === 0 && !providersError && (
                        <div className="text-center py-6">
                            <span className={`font-ui text-warning font-medium ${typeRole.body}`}>
                                {t('home.noProviders', 'No game providers available at the moment')}
                            </span>
                        </div>
                    )}
                    </section>
                </div>
            </div>

            <WinTicker items={tickerItems} className="shrink-0" />
        </main>

        {/* Order summary only when cart has amounts (not on pure guest browse) */}
        {totalAmount > 0 && (
            <OrderSummaryFooter
                className="flex-shrink-0"
                totalAmount={totalAmount}
                onClearOrder={handleResetAll}
                onProceedToFunding={handleProceedToFunding}
                onShowOrderSummary={() => setShowOrderSummary(true)}
            />
        )}

        {/* Order Summary Modal */}
        <OrderSummaryModal
            isOpen={showOrderSummary}
            onClose={() => setShowOrderSummary(false)}
            totalAmount={totalAmount}
            providerAmounts={providerAmounts}
            providers={providers}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onRemoveItem={handleRemoveItem}
            selectedProvider={null}
            selectedAction="deposit"
            onConfirm={() => {
                setShowOrderSummary(false);
                handleProceedToFunding();
            }}
        />

        {/* Manual Deposit Warning Modal */}
        <ManualDepositWarningModal
            isOpen={showManualDepositWarning}
            onClose={() => setShowManualDepositWarning(false)}
            onConfirm={executeProceedToFunding}
            amount={getManualProvidersTotalAmount()}
            providerName={getSelectedManualProviders().map(p => p.name).join(', ')}
        />

        {/* Wireframe mGame — .ov > .mdl width 640 (--wf-mdl-game-w) */}
        {detailRender && detailProvider && (
            <div
                className={`${MODAL_BACKDROP_SHELL_CLASS} ${detailBackdropClass}`}
                role="dialog"
                aria-modal="true"
            >
                <div
                    className={`${MODAL_PANEL_CLASS} ${detailPanelClass}`}
                    style={modalPanelStyle('--wf-mdl-game-w')}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="relative w-full">
                        <IconButton
                            variant="outline"
                            size="sm"
                            className="absolute top-0 right-0 z-10"
                            onClick={() => setDetailProvider(null)}
                            aria-label="Close"
                        >
                            <X />
                        </IconButton>
                        <div
                            className="flex items-center min-w-0 pr-10"
                            style={{ gap: 'calc(12px * var(--proto-scale))' }}
                        >
                            <div
                                className="lobby-gi-ico shrink-0 overflow-hidden"
                                style={{ width: 'var(--wf-gm-ico)', height: 'var(--wf-gm-ico)' }}
                            >
                                <img
                                    src={
                                        hasIconForProvider(detailProvider.code)
                                            ? detailProvider.icon
                                            : '/icons/egame.jpg'
                                    }
                                    alt=""
                                    className="block w-full h-full object-cover pointer-events-none select-none"
                                    draggable={false}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/icons/egame.jpg';
                                    }}
                                />
                            </div>
                            <div className="flex flex-col min-w-0 flex-1 justify-center" style={{ gap: 'calc(2px * var(--proto-scale))' }}>
                                <b className="font-display font-bold text-ink truncate" style={wfContinueStyle}>
                                    {detailProvider.name}
                                </b>
                                <span className="font-ui text-caption-muted truncate" style={wfMutedStyle}>
                                    {t('home.providerMeta', 'Provider · game')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div
                        className="grid grid-cols-3 w-full min-w-0"
                        style={{ gap: 'calc(8px * var(--proto-scale))' }}
                    >
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="min-w-0 rounded-[8px] border border-border-ice overflow-hidden bg-surface aspect-[178/105]"
                            >
                                {hasIconForProvider(detailProvider.code) ? (
                                    <img src={detailProvider.icon} alt="" className="w-full h-full object-cover opacity-90" />
                                ) : (
                                    <div className="w-full h-full bg-[linear-gradient(160deg,#1D3FA8_0%,#0A1B52_100%)]" />
                                )}
                            </div>
                        ))}
                    </div>

                    <p className="font-ui text-caption-muted m-0 w-full" style={wfMutedStyle}>
                        {detailProvider.tagline ||
                            t(
                                'home.gameDescription',
                                'Short description of the game — mechanics, volatility, min bet…'
                            )}
                    </p>
                    {detailProvider.depositType === 'manual' && (
                        <p className="inline-flex items-center gap-1.5 font-ui text-warning m-0" style={wfGamesSubStyle}>
                            <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            {t('home.manualDepositNote', 'Manual deposit — processed by staff (5 min – 2 hrs)')}
                        </p>
                    )}

                    <div
                        key={detailProvider.id}
                        className="wf-game-mdl-actions grid grid-cols-1 min-[520px]:grid-cols-3 w-full min-w-0"
                        style={{ gap: 'calc(10px * var(--proto-scale))' }}
                    >
                        <Button
                            variant="play"
                            size="md"
                            className={`btn-shine btn-shine--play w-full min-w-0 !px-3${waveGridEnabled ? ` ${ANIMATION_REGISTRY.wave.className}` : ''}`}
                            style={waveGridEnabled ? (getWaveTileStyle(0, 0) as React.CSSProperties) : undefined}
                            icon={<Play className="shrink-0" fill="currentColor" />}
                            onClick={() => {
                                const provider = detailProvider;
                                setDetailProvider(null);
                                if (provider) handlePlay(provider);
                            }}
                        >
                            {isAuthenticated
                                ? t('home.play', 'Play')
                                : t('home.playSignIn', 'Sign in to play')}
                        </Button>
                        <Button
                            variant="moneyIn"
                            size="md"
                            className={`btn-shine btn-shine--money-in w-full min-w-0 !px-3${waveGridEnabled ? ` ${ANIMATION_REGISTRY.wave.className}` : ''}`}
                            style={waveGridEnabled ? (getWaveTileStyle(0, 1) as React.CSSProperties) : undefined}
                            onClick={handleAddFundsFromDetail}
                        >
                            {t('home.addFundsToGame', '＋ Add funds')}
                        </Button>
                        <Button
                            variant="moneyOut"
                            size="md"
                            className={`btn-shine btn-shine--money-out w-full min-w-0 !px-3${waveGridEnabled ? ` ${ANIMATION_REGISTRY.wave.className}` : ''}`}
                            style={waveGridEnabled ? (getWaveTileStyle(0, 2) as React.CSSProperties) : undefined}
                            onClick={handleWithdrawFromDetail}
                        >
                            {t('home.withdrawFromGame', '↓ Withdraw')}
                        </Button>
                    </div>
                </div>
            </div>
        )}

        <GameWithdrawModal
            open={withdrawModalOpen}
            onClose={() => {
                setWithdrawModalOpen(false);
                setWithdrawProviderId(null);
            }}
            focusProviderId={withdrawProviderId}
            onSuccess={() => {
                if (!isAuthenticated) return;
                getUserGameWallets()
                    .then((response) => {
                        if (response.success && response.data?.items) {
                            setGameWallets(response.data.items);
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[Home] Error refreshing game wallets:', error);
                    });
            }}
        />
    </div>);
}

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines and uncomment the last line
// To enable demo mode: Uncomment the next 2 lines and comment out the last line
export default function KioskGameProviders() {
    return (
        <DemoWrapper>
            <KioskGameProvidersContent />
        </DemoWrapper>
    );
}

// PRODUCTION MODE - Use this when demo mode is disabled
// export default KioskGameProvidersContent;
